import React, { useEffect, useMemo, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ITEM_TYPES, typeKeyForApiType } from 'dashboard/cards/item-shape'

// A key that is unique across the three item types — ids are uuids per table, so a bare id is not.
export const linkedItemKey = (item) => `${item.item_type}-${item.id}`

const typeMeta = (apiType) => ITEM_TYPES[typeKeyForApiType(apiType)] || {}

// `id` has to key off the item, not its display text: a report family can hold two Samples with
// the same name, and duplicate ids make one row's label toggle the other row's checkbox.
const LinkedItemRow = ({ id, label, name, checked, disabled, note, onChange }) => (
  <Form.Check
    type="checkbox"
    id={id}
    checked={checked}
    disabled={disabled}
    onChange={onChange}
    className="mb-2"
    label={
      <span className="d-flex align-items-center gap-2">
        <span>{name}</span>
        <small className="text-muted">
          {label}
          {note ? `, ${note}` : ''}
        </small>
      </span>
    }
  />
)

// Views, Samples and Reports are filed as independent items, so moving one of them on its own
// leaves the rest of its report behind in the old folder. This asks before that happens.
const MoveLinkedItemsModal = ({
  show,
  onHide,
  item,
  itemApiType,
  folderName,
  relatedItems = [],
  onMoveSelected,
  onMoveOnly,
  isLoading
}) => {
  const [selectedKeys, setSelectedKeys] = useState([])

  // Everything starts checked: moving the whole report is the reason this prompt exists. Keyed on
  // `show` alone on purpose — re-running as `relatedItems` re-renders would undo the user's
  // unchecking mid-decision.
  const relatedKeys = relatedItems.map(linkedItemKey).join(',')
  useEffect(() => {
    if (show) setSelectedKeys(relatedKeys ? relatedKeys.split(',') : [])
  }, [show, relatedKeys])

  const sourceLabel = typeMeta(itemApiType).label || 'item'
  const selected = useMemo(
    () => relatedItems.filter((related) => selectedKeys.includes(linkedItemKey(related))),
    [relatedItems, selectedKeys]
  )

  const toggle = (related) => {
    const key = linkedItemKey(related)
    setSelectedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
  }

  const totalCount = selected.length + 1
  // A Report is the thing everything else hangs off, so it gets the other sentence: it is not
  // "used by" a report, it has linked items.
  const isReport = itemApiType === 'report'
  const reportCount = relatedItems.filter((related) => related.item_type === 'report').length
  const reportLabel = reportCount === 1 ? 'report' : 'reports'
  const linkedLabel = relatedItems.length === 1 ? 'linked item' : 'linked items'

  return (
    <Modal show={show} onHide={isLoading ? undefined : onHide} backdrop={isLoading ? 'static' : true} centered>
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2 fs-5">
          {typeMeta(itemApiType).icon && (
            <FontAwesomeIcon icon={typeMeta(itemApiType).icon} className="text-primary" />
          )}
          Move to {folderName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          {isReport ? (
            <>
              <strong>{item?.name}</strong> has {relatedItems.length} {linkedLabel}.
            </>
          ) : (
            <>
              <strong>{item?.name}</strong> is used by {reportCount} {reportLabel}.
            </>
          )}{' '}
          Move them into <strong>{folderName}</strong> too?
        </p>
        <div className="border rounded p-3 bg-light">
          <LinkedItemRow
            id={`linked-source-${itemApiType}-${item?.id}`}
            label={sourceLabel}
            name={item?.name}
            note="this item"
            checked
            disabled
            onChange={() => {}}
          />
          {relatedItems.map((related) => (
            <LinkedItemRow
              key={linkedItemKey(related)}
              id={`linked-${linkedItemKey(related)}`}
              label={typeMeta(related.item_type).label || 'Item'}
              name={related.name}
              checked={selectedKeys.includes(linkedItemKey(related))}
              onChange={() => toggle(related)}
            />
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0 d-flex flex-column gap-2">
        <Button
          variant="primary"
          className="w-100"
          onClick={() => onMoveSelected(selected)}
          disabled={isLoading || selected.length === 0}
        >
          Move {totalCount} items
        </Button>
        <Button
          variant="light"
          className="w-100 text-primary"
          onClick={onMoveOnly}
          disabled={isLoading}
        >
          Move only this {sourceLabel}
        </Button>
        <Button
          variant="light"
          className="w-100 text-primary"
          onClick={onHide}
          disabled={isLoading}
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default MoveLinkedItemsModal
